export interface InputFrame {
  steer: number;
  push: boolean;
  brake: boolean;
  jump: boolean;
  flip: boolean;
  grab: boolean;
  punch: boolean;
  kick: boolean;
  reset: boolean;
  scoreboard: boolean;
  mute: boolean;
  menu: boolean;
  help: boolean;
}

const BINDINGS = {
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  push: ['KeyW', 'ArrowUp'],
  brake: ['KeyS', 'ArrowDown'],
  jump: ['Space'],
  flip: ['KeyJ'],
  grab: ['KeyL', 'ShiftLeft', 'ShiftRight'],
  punch: ['KeyP'],
  kick: ['KeyK'],
  reset: ['KeyR'],
  scoreboard: ['Tab'],
  mute: ['KeyM'],
  menu: ['Escape'],
  help: ['KeyH'],
} as const;

const CAPTURED = new Set<string>([
  'Space',
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
]);

/** Keyboard + standard gamepad input with edge latching so taps between frames are kept. */
export class Input {
  private readonly held = new Set<string>();
  private readonly tapped = new Set<string>();
  private readonly padPrevious: boolean[] = [];
  private enabled = true;

  public constructor(private readonly target: Window = window) {
    target.addEventListener('keydown', this.onKeyDown);
    target.addEventListener('keyup', this.onKeyUp);
    target.addEventListener('blur', this.onBlur);
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.held.clear();
      this.tapped.clear();
    }
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const element = event.target as HTMLElement | null;
    if (element && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')) return;
    if (!this.enabled && event.code !== 'Escape') return;
    if (CAPTURED.has(event.code)) event.preventDefault();
    if (!event.repeat) this.tapped.add(event.code);
    this.held.add(event.code);
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.held.delete(event.code);
  };

  private readonly onBlur = (): void => {
    this.held.clear();
  };

  private down(codes: readonly string[]): boolean {
    return codes.some((code) => this.held.has(code) || this.tapped.has(code));
  }

  private tap(codes: readonly string[]): boolean {
    return codes.some((code) => this.tapped.has(code));
  }

  public poll(): InputFrame {
    const frame: InputFrame = {
      steer: (this.down(BINDINGS.right) ? 1 : 0) - (this.down(BINDINGS.left) ? 1 : 0),
      push: this.down(BINDINGS.push),
      brake: this.down(BINDINGS.brake),
      jump: this.down(BINDINGS.jump),
      flip: this.tap(BINDINGS.flip),
      grab: this.down(BINDINGS.grab),
      punch: this.tap(BINDINGS.punch),
      kick: this.tap(BINDINGS.kick),
      reset: this.tap(BINDINGS.reset),
      scoreboard: this.down(BINDINGS.scoreboard),
      mute: this.tap(BINDINGS.mute),
      menu: this.tap(BINDINGS.menu),
      help: this.tap(BINDINGS.help),
    };
    this.tapped.clear();
    this.mergeGamepad(frame);
    return frame;
  }

  private mergeGamepad(frame: InputFrame): void {
    const pads =
      typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : [];
    const pad = [...pads].find((p) => p && p.mapping === 'standard');
    if (!pad) return;
    const pressed = (index: number) => pad.buttons[index]?.pressed ?? false;
    const edge = (index: number) => {
      const now = pressed(index);
      const was = this.padPrevious[index] ?? false;
      this.padPrevious[index] = now;
      return now && !was;
    };
    const stickX = pad.axes[0] ?? 0;
    const stickY = pad.axes[1] ?? 0;
    if (Math.abs(stickX) > 0.2) frame.steer = Math.max(-1, Math.min(1, stickX));
    if (pressed(14)) frame.steer = -1;
    if (pressed(15)) frame.steer = 1;
    frame.push ||= stickY < -0.5 || pressed(7) || pressed(12);
    frame.brake ||= stickY > 0.5 || pressed(6) || pressed(13);
    frame.jump ||= pressed(0);
    frame.flip ||= edge(2);
    frame.grab ||= pressed(1);
    frame.punch ||= edge(5);
    frame.kick ||= edge(3);
    frame.reset ||= edge(4);
    frame.scoreboard ||= pressed(8);
    frame.menu ||= edge(9);
  }

  public dispose(): void {
    this.target.removeEventListener('keydown', this.onKeyDown);
    this.target.removeEventListener('keyup', this.onKeyUp);
    this.target.removeEventListener('blur', this.onBlur);
  }
}
